/**
 * API Keys Modal Component
 * 
 * Business 플랜용 API 키 발급 및 관리
 */

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../i18n';
import { useAuth } from '../auth';
import { useBodyScrollLock } from '../hooks';
import { listApiKeys, createApiKey, revokeApiKey, type ApiKeyInfo } from '../api';
import ApiGuideModal from './ApiGuideModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ApiKeysModal({ isOpen, onClose }: Props) {
  const { t, lang } = useLanguage();
  const { user, quota } = useAuth();
  
  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  const [maxKeys, setMaxKeys] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 사용량 정보
  const [totalDailyUsage, setTotalDailyUsage] = useState(0);
  const [totalDailyLimit, setTotalDailyLimit] = useState(200);
  
  // 키 생성 폼
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // 생성된 키 표시 (한 번만!)
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  // 폐기 확인
  const [revokeTarget, setRevokeTarget] = useState<number | null>(null);
  
  // effective plan 사용 (팀 멤버도 팀 플랜 혜택 적용)
  const isTeamPlan = quota?.plan === 'team' || user?.subscription_plan === 'team';
  
  // 팀 오너인지 확인 (팀 정보가 quota에 있음)
  const isTeamOwner = quota?.team?.is_owner === true;
  const isInTeam = !!user?.team_id;
  
  // 생성 권한: Team 플랜의 팀 오너만
  const canCreate = isTeamPlan && isTeamOwner;
  
  // 가이드 표시 상태
  const [showGuide, setShowGuide] = useState(false);
  
  // 다운그레이드 상태: 이전에 키를 발급받았지만 현재 Team이 아닌 경우
  const [hasExistingKeys, setHasExistingKeys] = useState(false);
  const isKeysFrozen = hasExistingKeys && !isTeamPlan;

  const loadKeys = useCallback(async () => {
    // Team이 아니더라도 기존 키가 있는지 확인하기 위해 로드 시도
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await listApiKeys();
      setKeys(response.keys);
      setMaxKeys(response.max_keys);
      setTotalDailyUsage(response.total_daily_usage ?? 0);
      setTotalDailyLimit(response.total_daily_limit ?? 200);
      // 기존 키가 있는지 확인
      if (response.keys.length > 0) {
        setHasExistingKeys(true);
      }
    } catch (err) {
      // Team이 아닌 경우 403 에러가 올 수 있음 - 정상 케이스로 처리
      if (!isTeamPlan) {
        setHasExistingKeys(false);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load API keys');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isTeamPlan]);

  useEffect(() => {
    if (isOpen) {
      // 모달 열릴 때 이전 생성 키 상태 초기화
      setCreatedKey(null);
      setCopied(false);
      loadKeys();
    }
  }, [isOpen, loadKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    
    setIsCreating(true);
    setError(null);
    
    try {
      const response = await createApiKey(newKeyName.trim());
      setCreatedKey(response.key);
      setNewKeyName('');
      await loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRevoke = async (keyId: number) => {
    try {
      await revokeApiKey(keyId);
      setRevokeTarget(null);
      await loadKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t.apiKeys.never;
    const date = new Date(dateStr);
    return date.toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const activeKeys = keys.filter(k => !k.is_revoked);
  const canCreateMore = activeKeys.length < maxKeys;

  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 백드롭 */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* 모달 */}
      <div className="relative bg-terminal-bg border border-terminal-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b border-terminal-border bg-terminal-dim/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-terminal-text flex items-center gap-2">
                <svg className="w-5 h-5 text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                {t.apiKeys.title}
              </h2>
              <p className="text-sm text-terminal-muted mt-0.5">{t.apiKeys.subtitle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-terminal-muted hover:text-terminal-text transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* 컨텐츠 */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* 사용량 경고 배너 */}
          {isTeamPlan && !isLoading && keys.length > 0 && (() => {
            const usagePercent = totalDailyLimit > 0 ? (totalDailyUsage / totalDailyLimit) * 100 : 0;
            
            // 100% 이상: 빨간색 (초과)
            if (usagePercent >= 100) {
              return (
                <div className="p-4 bg-status-danger/10 border border-status-danger/30 rounded-lg mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-status-danger/20 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-status-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-status-danger">
                        {lang === 'ko' ? '일일 API 사용량 초과' : 'Daily API Limit Exceeded'}
                      </h4>
                      <p className="text-xs text-terminal-muted mt-1">
                        {lang === 'ko' 
                          ? `오늘 ${totalDailyUsage.toLocaleString()}회 사용 (한도: ${totalDailyLimit.toLocaleString()}회). 00:00 KST에 리셋됩니다.`
                          : `Used ${totalDailyUsage.toLocaleString()} today (Limit: ${totalDailyLimit.toLocaleString()}). Resets at 00:00 KST.`}
                      </p>
                    </div>
                    <div className="text-2xl font-bold text-status-danger">
                      {Math.min(Math.round(usagePercent), 100)}%
                    </div>
                  </div>
                  <div className="mt-3 h-2 bg-terminal-dim rounded-full overflow-hidden">
                    <div className="h-full bg-status-danger" style={{ width: '100%' }} />
                  </div>
                </div>
              );
            }
            
            // 80% 이상: 주황색 (경고)
            if (usagePercent >= 80) {
              return (
                <div className="p-4 bg-status-warning/10 border border-status-warning/30 rounded-lg mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-status-warning/20 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-status-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-status-warning">
                        {lang === 'ko' ? '일일 API 사용량 경고' : 'Daily API Usage Warning'}
                      </h4>
                      <p className="text-xs text-terminal-muted mt-1">
                        {lang === 'ko' 
                          ? `오늘 ${totalDailyUsage.toLocaleString()}회 사용 (한도: ${totalDailyLimit.toLocaleString()}회)`
                          : `Used ${totalDailyUsage.toLocaleString()} today (Limit: ${totalDailyLimit.toLocaleString()})`}
                      </p>
                    </div>
                    <div className="text-2xl font-bold text-status-warning">
                      {Math.round(usagePercent)}%
                    </div>
                  </div>
                  <div className="mt-3 h-2 bg-terminal-dim rounded-full overflow-hidden">
                    <div className="h-full bg-status-warning" style={{ width: `${usagePercent}%` }} />
                  </div>
                </div>
              );
            }
            
            // 0% 이상: 초록색 (정상) - 간단한 프로그레스 바만 표시
            return (
              <div className="p-3 bg-terminal-dim/30 border border-terminal-border rounded-lg mb-4">
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-terminal-muted">
                    {lang === 'ko' ? '일일 API 사용량' : 'Daily API Usage'}
                  </span>
                  <span className="text-terminal-text font-medium">
                    {totalDailyUsage.toLocaleString()} / {totalDailyLimit.toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-terminal-dim rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-status-go transition-all" 
                    style={{ width: `${Math.max(usagePercent, 1)}%` }} 
                  />
                </div>
              </div>
            );
          })()}

          {/* Team 플랜 아님 & 기존 키도 없음 */}
          {!isTeamPlan && !hasExistingKeys && !isLoading && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-status-warning/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-status-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-terminal-text mb-2">
                {t.apiKeys.teamRequired}
              </h3>
              <p className="text-terminal-muted text-sm">
                {t.apiKeys.teamRequiredDesc}
              </p>
            </div>
          )}
          
          {/* 다운그레이드 상태: 기존 키가 있지만 Team이 아님 */}
          {isKeysFrozen && (
            <div className="p-4 bg-status-holding/10 border border-status-holding/30 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-status-holding/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-status-holding" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-status-holding">
                    {t.downgrade.apiKeysFrozen}
                  </h4>
                  <p className="text-xs text-terminal-muted mt-1">
                    {t.downgrade.apiKeysFrozenDesc}
                  </p>
                  <p className="text-xs text-terminal-muted mt-1 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5 text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t.downgrade.apiKeysDataPreserved}
                  </p>
                  <button
                    onClick={onClose}
                    className="mt-3 px-4 py-2 bg-status-holding/20 hover:bg-status-holding/30 border border-status-holding/50 text-status-holding rounded-lg text-sm font-medium transition-colors"
                  >
                    {t.downgrade.upgradePlan}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Team 플랜이거나 기존 키가 있는 경우 (frozen 포함) */}
          {(isTeamPlan || hasExistingKeys) && (
            <>
              {/* 에러 메시지 */}
              {error && (
                <div className="mb-4 p-3 bg-status-danger/10 border border-status-danger/30 rounded text-status-danger text-sm">
                  {error}
                </div>
              )}

              {/* 생성된 키 표시 (한 번만!) */}
              {createdKey && (
                <div className="mb-6 p-4 bg-status-go/10 border border-status-go/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-status-go/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-status-go font-medium mb-1">{t.apiKeys.keyCreated}</h4>
                      <p className="text-terminal-muted text-xs mb-3">{t.apiKeys.keyCreatedWarning}</p>
                      
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 bg-terminal-bg border border-terminal-border rounded font-mono text-sm text-terminal-text truncate">
                          {createdKey}
                        </code>
                        <button
                          onClick={handleCopy}
                          className={`px-3 py-2 rounded font-medium text-sm transition-colors flex items-center gap-1.5 ${
                            copied
                              ? 'bg-status-go text-white'
                              : 'bg-terminal-dim hover:bg-terminal-border text-terminal-text'
                          }`}
                        >
                          {copied ? (
                            <>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {t.apiKeys.copied}
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              {t.apiKeys.copyKey}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => setCreatedKey(null)}
                      className="text-terminal-muted hover:text-terminal-text transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* API 사용 가이드 버튼 */}
              {(isTeamPlan || hasExistingKeys) && !isLoading && (
                <button
                  onClick={() => setShowGuide(true)}
                  className="w-full mb-4 p-4 bg-status-go/5 border border-status-go/20 rounded-lg flex items-center gap-3 hover:bg-status-go/10 transition-colors group text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-status-go/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-status-go group-hover:underline">{t.apiKeys.usageGuide}</h4>
                    <p className="text-xs text-terminal-muted mt-0.5">{t.apiKeys.usageGuideDesc}</p>
                  </div>
                  <svg className="w-4 h-4 text-terminal-muted group-hover:text-status-go transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* 멤버용 안내 메시지 */}
              {isInTeam && !isTeamOwner && isTeamPlan && !isKeysFrozen && (
                <div className="mb-4 p-3 bg-terminal-dim/30 border border-terminal-border rounded-lg">
                  <div className="flex items-center gap-2 text-terminal-muted text-sm">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t.apiKeys.memberViewOnly}
                  </div>
                </div>
              )}

              {/* 키 생성 폼 - frozen 상태 또는 멤버인 경우 숨김 */}
              {canCreateMore && !createdKey && !isKeysFrozen && canCreate && (
                <div className="mb-6 p-4 bg-terminal-dim/20 border border-terminal-border rounded-lg">
                  <h3 className="text-sm font-medium text-terminal-text mb-3">{t.apiKeys.createKey}</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder={t.apiKeys.keyNamePlaceholder}
                      className="flex-1 px-3 py-2 bg-terminal-bg border border-terminal-border rounded text-terminal-text text-sm focus:outline-none focus:border-status-go"
                      maxLength={100}
                    />
                    <button
                      onClick={handleCreate}
                      disabled={!newKeyName.trim() || isCreating}
                      className="px-4 py-2 bg-status-go text-white rounded font-medium text-sm hover:bg-status-go/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCreating ? t.apiKeys.creating : t.apiKeys.create}
                    </button>
                  </div>
                </div>
              )}

              {/* 키 목록 */}
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-status-go border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-terminal-muted text-sm">Loading...</p>
                </div>
              ) : activeKeys.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-terminal-dim/30 flex items-center justify-center">
                    <svg className="w-8 h-8 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <h3 className="text-terminal-text font-medium mb-1">{t.apiKeys.noKeys}</h3>
                  <p className="text-terminal-muted text-sm">{t.apiKeys.noKeysDesc}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm text-terminal-muted mb-2">
                    <span>API Keys ({activeKeys.length}/{maxKeys})</span>
                  </div>
                  
                  {activeKeys.map((key) => (
                    <div
                      key={key.id}
                      className="p-4 bg-terminal-dim/20 border border-terminal-border rounded-lg"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-terminal-text">{key.name}</span>
                            <code className="text-xs px-1.5 py-0.5 bg-terminal-bg border border-terminal-border rounded text-terminal-muted font-mono">
                              {key.key_prefix}...
                            </code>
                          </div>
                          
                          <div className="flex items-center gap-4 text-xs text-terminal-muted">
                            <span>
                              {t.apiKeys.dailyUsage}: {key.daily_usage}/{key.daily_limit}
                            </span>
                            <span>
                              {t.apiKeys.lastUsed}: {formatDate(key.last_used_at)}
                            </span>
                          </div>
                        </div>
                        
                        {/* 폐기 버튼 - frozen 상태 또는 멤버인 경우 숨김 */}
                        {!isKeysFrozen && canCreate && (
                          revokeTarget === key.id ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleRevoke(key.id)}
                                className="px-3 py-1 bg-status-danger text-white rounded text-xs font-medium hover:bg-status-danger/90 transition-colors"
                              >
                                {t.team.confirm}
                              </button>
                              <button
                                onClick={() => setRevokeTarget(null)}
                                className="px-3 py-1 bg-terminal-dim text-terminal-text rounded text-xs font-medium hover:bg-terminal-border transition-colors"
                              >
                                {t.team.cancel}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setRevokeTarget(key.id)}
                              className="px-3 py-1 text-status-danger text-xs font-medium hover:bg-status-danger/10 rounded transition-colors"
                            >
                              {t.apiKeys.revokeKey}
                            </button>
                          )
                        )}
                        {/* Frozen 상태 표시 */}
                        {isKeysFrozen && (
                          <span className="px-2 py-1 bg-status-holding/20 text-status-holding text-xs rounded">
                            {t.downgrade.featureLocked.split(' ').slice(0, 2).join(' ')}...
                          </span>
                        )}
                      </div>
                      
                      {/* 사용량 바 */}
                      <div className="mt-3">
                        <div className="h-1.5 bg-terminal-dim rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              key.daily_usage / key.daily_limit > 0.9
                                ? 'bg-status-danger'
                                : key.daily_usage / key.daily_limit > 0.7
                                ? 'bg-status-warning'
                                : 'bg-status-go'
                            }`}
                            style={{ width: `${Math.min((key.daily_usage / key.daily_limit) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 최대 키 도달 안내 */}
              {!canCreateMore && !createdKey && (
                <div className="mt-4 p-3 bg-status-warning/10 border border-status-warning/30 rounded text-status-warning text-sm text-center">
                  {t.apiKeys.maxKeysReached.replace('{max}', String(maxKeys))}
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {/* API 가이드 모달 */}
      <ApiGuideModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
      />
    </div>
  );
}

